<script setup>
/**
 * Aceleres: a figurinha vetorial desenhada, sem balão em volta.
 *
 * Serve tanto a bolha da conversa quanto as miniaturas da citação (dentro do
 * balão e no card do compositor) — quem manda no tamanho é quem usa.
 *
 * O motor é o de CANVAS. O de SVG desenha a animação como centenas de nós no
 * DOM e reescreve todos a cada quadro: com uma figurinha de 394 quadros a
 * 60 fps isso derrete a CPU da máquina do atendente.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import {
  loadLottiePlayer,
  fetchLottieAnimation,
} from 'dashboard/helper/lottieStickerPlayer';

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  replayOnHover: {
    type: Boolean,
    default: false,
  },
  continuous: {
    type: Boolean,
    default: false,
  },
});

// Aceleres 2026-08-17: a figurinha animada roda 3 vezes e para, a mesma regra
// que o GIF ja segue na bolha (ver GIF_MAX_LOOPS em Video.vue). Antes era
// `loop: true` — animacao infinita em cada figurinha da conversa, puxando CPU
// sem parar e disputando a atencao de quem le.
//
// No lottie-web o numero e a quantidade TOTAL de voltas: ao chegar no fim ele
// soma 1 em `playCount` e para quando `playCount === loop`. Entao 3 = 3
// voltas, nao 3 repeticoes depois da primeira.
const STICKER_MAX_LOOPS = 3;

const container = ref(null);
const hasError = ref(false);
let animation = null;
let visibilityObserver = null;
let hasFinishedLoops = false;

const replayFinishedAnimation = () => {
  if (!props.replayOnHover || !animation || !hasFinishedLoops) return;

  hasFinishedLoops = false;
  animation.goToAndPlay(0, true);
};

// Só anima o que está em tela: uma conversa com dez figurinhas antigas não tem
// por que gastar CPU desenhando as que ficaram para trás na rolagem.
const watchVisibility = () => {
  if (typeof IntersectionObserver === 'undefined' || !container.value) return;

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (!animation) return;
      if (!entry.isIntersecting) {
        animation.pause();
        return;
      }
      // Gastas as 3 voltas, `play()` retomaria no ultimo quadro e terminaria
      // na hora — parado na pratica. Sair da tela e voltar reinicia a
      // contagem, igual ao GIF; `goToAndPlay(0, true)` zera o `playCount`.
      if (hasFinishedLoops) {
        hasFinishedLoops = false;
        animation.goToAndPlay(0, true);
        return;
      }
      animation.play();
    },
    { threshold: 0.1 }
  );
  visibilityObserver.observe(container.value);
};

onMounted(async () => {
  if (!props.url || !container.value) return;

  try {
    const [player, animationData] = await Promise.all([
      loadLottiePlayer(),
      fetchLottieAnimation(props.url),
    ]);
    if (!container.value) return;

    animation = player.loadAnimation({
      container: container.value,
      renderer: 'canvas',
      loop: props.continuous ? true : STICKER_MAX_LOOPS,
      autoplay: true,
      animationData,
      rendererSettings: { clearCanvas: true },
    });
    // Sem interpolação entre quadros: o desenho é o mesmo e o trabalho cai
    // pela metade.
    animation.setSubframe(false);
    if (!props.continuous) {
      animation.addEventListener('complete', () => {
        hasFinishedLoops = true;
      });
    }
    watchVisibility();
  } catch (error) {
    hasError.value = true;
  }
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
  visibilityObserver = null;
  animation?.destroy();
  animation = null;
});
</script>

<template>
  <!-- Sem `relative` aqui de propósito: `.acl-sticker-bubble div.relative`
       (aceleres-pill-icons.css) fixa a moldura de 237,5px da figurinha, e ela
       pegaria também a miniatura da citação. Quem quer a moldura pede a
       classe — ver LottieSticker.vue. -->
  <div
    v-if="!hasError"
    ref="container"
    class="overflow-hidden rounded-lg"
    @mouseenter="replayFinishedAnimation"
  />
  <span v-else class="block px-2 py-1 text-sm text-n-slate-11">
    Figurinha indisponível
  </span>
</template>
